import { useQuery } from '@tanstack/react-query';
import { container } from '@src/container';
import { AuthService } from '@src/application/users/usecases';

/**
 * Hook: useCurrentUser
 * Fetches the currently authenticated user
 * Auth state is determined by successful user fetch, not token presence
 * 
 * @example
 * const { data: user, isLoading, error } = useCurrentUser();
 */
export function useCurrentUser() {
    const authService = container.resolve<AuthService>('authService');

    return useQuery({
        queryKey: ['auth', 'currentUser'],
        queryFn: async () => {
            const user = await authService.getCurrentUser();
            if (import.meta.env.DEV) {
                console.log('✅ Current user fetched:', user);
            }
            return user;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
        // Don't retry on 401 errors (not authenticated)
        // Do retry on network errors or 5xx errors
        retry: (failureCount, error: any) => {
            // Don't retry on 401 - user is not authenticated
            if (error?.status === 401) {
                return false;
            }
            // Retry once on network errors or server errors
            if (failureCount < 1) {
                return true;
            }
            return false;
        },
        retryDelay: 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always', // Always refetch on mount to ensure fresh data
    });
}
